// 재내원 안내 「다음 시기 변경 / 다음 안내 시기」 모달 (2026-10-01) — ClinicRecalls 행 버튼·ClinicPatientDetail 진입이 같은 모달을 쓴다.
// 서버 계약 docs/계약_리콜_서버_2026-10-01.md §B-1: 안내 종류(트리거 코드) + 개월 프리셋(1/3/6/12/24) 또는 직접 날짜. 하한 7일·상한 24개월.
// 자유 문구·수동 발송·치료명 원문 입력은 없다. source 는 'manual'(같은 종류의 진행 중 안내는 교체됨).
// 2026-10-08 알림 파트(기획 §4-1 A-1 효과 ⑴⑵): 초기값 = 치과 기본 설정(getClinicRecallDefaults) — 기본 주기, 꺼 둔 종류는 선택지에서 숨김
//   (단 initialTrigger 로 들어온 종류는 기존 안내 변경이므로 꺼져 있어도 남긴다 — 소급 없음). 조회 실패 시 기존 동작(R1·6개월) 그대로 + 1줄 안내.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import { TRIGGER_CODES, triggerLabel, setPatientRecall, describeRecallError, dayKey, addMonths, daysAhead, fmtDay, DUE_MIN_DAYS, DUE_MAX_DAYS, clinicSendBlockers, getClinicRecallDefaults, describeRecallDefaultsError } from '../../services/clinicRecallApi';

const PRESETS = [1, 3, 6, 12, 24];

function RecallScheduleModal({ patientId, patientName, initialTrigger, onClose, onSaved }) {
    // 훅은 전부 최상단 — early return 위
    const [trigger, setTrigger] = useState(TRIGGER_CODES.includes(initialTrigger) ? initialTrigger : 'R1');
    const [mode, setMode] = useState('preset');     // 'preset' | 'date'
    const [months, setMonths] = useState(6);
    const [customDay, setCustomDay] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [blockers, setBlockers] = useState([]);   // 계약 B-1(5차 R-P2-7) 저장 응답 clinicPhoneMissing·clinicNameMissing·clinicNameBanned → 저장 후 안내 1줄씩(닫지 않고 보여줌)

    const [defaults, setDefaults] = useState(null);   // { defaults, catalog } — 로딩 전·실패 시 null(기존 동작)
    const [defaultsNote, setDefaultsNote] = useState('');
    const [touched, setTouched] = useState(false);     // 사용자가 직접 고른 뒤에는 기본값으로 덮어쓰지 않는다

    const today = useMemo(() => new Date(), []);

    // 트리거의 기본 주기를 화면 상태로 — months 단위는 프리셋, 일·주 단위는 직접 날짜(오늘 + 주기)
    const applyDefault = (code, d = defaults) => {
        if (!d) return;
        const c = d.catalog?.[code]; const p = d.defaults?.[code]?.period;
        if (!c || !Number.isFinite(p)) return;
        if (c.unit === 'months' && PRESETS.includes(p)) { setMode('preset'); setMonths(p); return; }
        const days = c.unit === 'months' ? null : (c.unit === 'weeks' ? p * 7 : p);
        const day = days == null ? dayKey(addMonths(today, p)) : dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + days));
        setMode('date'); setCustomDay(day);
    };

    useEffect(() => {
        let alive = true;
        getClinicRecallDefaults()
            .then(r => {
                if (!alive) return;
                setDefaults(r);
                // 초기 트리거가 꺼진 종류면(새 등록일 때만) 켜진 첫 종류로
                const init = TRIGGER_CODES.includes(initialTrigger) ? initialTrigger : null;
                let code = init || 'R1';
                if (!init && r.defaults?.[code]?.enabled === false) code = TRIGGER_CODES.find(c => r.defaults?.[c]?.enabled !== false) || code;
                if (!touched) { setTrigger(code); applyDefault(code, r); }
            })
            .catch(e => { if (alive) setDefaultsNote(describeRecallDefaultsError(e, '치과 기본 설정을 불러오지 못해 기본값(6개월)으로 표시합니다.')); });
        return () => { alive = false; };
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    // 꺼 둔 종류는 숨김(단 지금 고른 종류·기존 안내 종류는 유지)
    const triggerOptions = TRIGGER_CODES.filter(c => !defaults || defaults.defaults?.[c]?.enabled !== false || c === trigger || c === initialTrigger);
    const allOff = !!defaults && TRIGGER_CODES.every(c => defaults.defaults?.[c]?.enabled === false) && !TRIGGER_CODES.includes(initialTrigger);
    const minDay = useMemo(() => dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + DUE_MIN_DAYS)), [today]);
    const maxDay = useMemo(() => dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + DUE_MAX_DAYS)), [today]);
    const dueDay = mode === 'preset' ? dayKey(addMonths(today, months)) : customDay;
    const ahead = daysAhead(dueDay);
    const rangeOk = Number.isFinite(ahead) && ahead >= DUE_MIN_DAYS && ahead <= DUE_MAX_DAYS;

    const submit = async () => {
        if (busy) return;
        if (!rangeOk) { setError(`안내 예정일은 오늘부터 ${DUE_MIN_DAYS}일 이후 ~ 24개월 이내여야 합니다.`); return; }
        setBusy(true); setError('');
        try {
            const res = await setPatientRecall({ patientId, trigger, months: mode === 'preset' ? months : 0, dueDay, source: 'manual' });
            if (!res?.ok) throw new Error('응답이 올바르지 않습니다');
            onSaved && onSaved(res);
            const b = clinicSendBlockers(res);
            if (b.length) {
                // 대표전화·치과명 미등록, 치과명 금지어면 닫지 않고 안내 — 사용자가 읽고 닫는다
                setBlockers(b); setBusy(false);
                return;
            }
            onClose();
        } catch (e) {
            setError(describeRecallError(e));
            setBusy(false);
        }
    };

    const chip = (active) => ({ ...U.buttonSecondary, padding: '5px 12px', fontSize: 13, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#334155', borderColor: active ? '#4f46e5' : '#e2e8f0' });

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => !busy && onClose()}>
            <div style={{ ...U.card, width: '100%', maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarClock size={16} color="#6366f1" /> 다음 안내 시기{patientName ? ` · ${patientName}` : ''}
                </h3>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>같은 종류의 진행 중인 안내가 있으면 새 시기로 교체됩니다. 안내는 앱을 설치하고 재내원 안내에 동의한 환자에게만 갑니다.</div>

                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>안내 종류</div>
                {defaultsNote && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{defaultsNote}</div>}
                {allOff && <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '6px 10px', marginBottom: 6 }}>{'치과 기본 설정에서 모든 안내 종류가 꺼져 있습니다. 환자 자동관리 › 알림에서 켤 수 있습니다.'}</div>}
                <select value={trigger} onChange={(e) => { setTouched(true); setTrigger(e.target.value); applyDefault(e.target.value); }} disabled={busy}
                    style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 12, background: '#fff' }}>
                    {triggerOptions.map(c => <option key={c} value={c}>{triggerLabel(c)}{defaults?.defaults?.[c]?.enabled === false ? ' (기본 설정에서 꺼짐)' : ''}</option>)}
                </select>

                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>시기</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {PRESETS.map(m => (
                        <button key={m} type="button" disabled={busy} onClick={() => { setTouched(true); setMode('preset'); setMonths(m); }} style={chip(mode === 'preset' && months === m)}>{m}개월 뒤</button>
                    ))}
                    <button type="button" disabled={busy} onClick={() => { setTouched(true); setMode('date'); }} style={chip(mode === 'date')}>직접 날짜</button>
                </div>
                {mode === 'date' && (
                    <input type="date" value={customDay} min={minDay} max={maxDay} disabled={busy} onChange={(e) => { setTouched(true); setCustomDay(e.target.value); }}
                        style={{ padding: '8px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8 }} />
                )}
                <div style={{ fontSize: 13, color: rangeOk ? '#0f172a' : '#b45309', background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                    안내 예정일: <b>{dueDay ? fmtDay(dueDay) : '-'}</b>{Number.isFinite(ahead) ? ` (${ahead}일 뒤)` : ''}
                    {!rangeOk && dueDay && <div style={{ fontSize: 12, marginTop: 2 }}>{`오늘부터 ${DUE_MIN_DAYS}일 이후 ~ 24개월 이내만 가능합니다.`}</div>}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>예정 7일 전·당일·14일 뒤·60일 뒤 최대 4회, 환자가 응답하면 중단됩니다. 발송 시각 매일 오전 10시 10분.</div>

                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 10 }}>{error}</div>}
                {blockers.length > 0 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>
                        {blockers.map(k => (
                            <div key={k}>
                                {k === 'phone' && <>저장되었지만 대표전화가 등록되지 않아 안내가 발송되지 않습니다. <Link to="/profile" style={{ color: '#b45309', fontWeight: 600, textDecoration: 'underline' }}>설정 › 프로필</Link>에서 대표전화를 등록하면 예정일부터 발송됩니다.</>}
                                {k === 'name' && <>저장되었지만 치과 이름이 등록되지 않아 안내가 발송되지 않습니다. <Link to="/profile" style={{ color: '#b45309', fontWeight: 600, textDecoration: 'underline' }}>설정 › 프로필</Link>에서 치과 이름(업체명)을 등록하면 예정일부터 발송됩니다.</>}
                                {k === 'banned' && <>저장되었지만 치과 이름에 사용할 수 없는 표현이 있어 안내가 보류됩니다. 덴탈클러스터에 문의해 주세요.</>}
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button type="button" disabled={busy} onClick={onClose} style={U.buttonSecondary}>{blockers.length ? '닫기' : '취소'}</button>
                    {!blockers.length && <button type="button" disabled={busy || !rangeOk} onClick={submit} style={{ ...U.buttonPrimary, opacity: (busy || !rangeOk) ? 0.6 : 1 }}>{busy ? '저장 중...' : '저장'}</button>}
                </div>
            </div>
        </div>
    );
}

export default RecallScheduleModal;
